/**
 * Angular imports.
 */
import { Component, EventEmitter, OnInit, Output, Input, SimpleChange, OnChanges, TemplateRef, ChangeDetectionStrategy, ChangeDetectorRef, SimpleChanges, HostListener } from '@angular/core';
import { Router } from '@angular/router';
/**
 * Rxjs imports.
 */
import { Subject, Subscription } from 'rxjs';
import { debounceTime, last } from 'rxjs/operators';

/**
 * Service imports.
 */
import { TimeService } from './services/timer.service';
import { NewLanguageService } from '../dataservices/new-language/new-language.service';
import { UserStoryService } from '../dataservices/userStory/user-story.service';
import { GetService } from '../dataservices/get.service';
import { AnalyticsEventService } from '../analytics-event/analytics-event.service';
import { FunCollectionService } from '../common/common-functions/fun-collection.service';

/**
 * Custom Decorator import.
 */
import { OnChange } from '../custom-decorators/ngOnchanges.Decorator';

/**
 * Constant imports.
 */
import { ACTION_ON, ACTION_TYPE, ALL_STORIES_END, CTA_TYPE, NO_INFO_TYPE, PRODUCT_TYPE_CONSTANT, PRODUCT_TYPE_CONSTANT_NAME, SCREEN_NAME, STORY_DEFAULT_DURATION, STORY_HEADER_ACTIONS, STORY_TYPES } from '../constants/app.constants';

import { PostApiService } from '../dataservices/post-api.service';
import { EVENT_NAME } from '../analytics-event/analytics.constant';
import { EventDataKey } from '../analytics-event/analytics.model';
import { DETAIL_ENTRY, DETAIL_EXIT, ENTRY, LIKE } from 'analytics2.0/dist/core/constants/analytic.interface';

/**
 * SDK IMPORTS
 */
import { Analytics } from 'analytics2.0';

@Component({
  selector: 'app-stories',
  templateUrl: './stories.component.html',
  styleUrls: ['./stories.component.scss'],
  providers: [TimeService],
})
export class StoriesComponent implements OnInit {

  /**
   * Stories Information.
   * Active index of the story.
   * Custom template for the story.
   * Default duration.
   */
  @Input() stories;
  @Input() viewListByDefault;
  @Input() activeIndex = 0;
  @Input() customComponent: TemplateRef<HTMLElement>;
  @Input() defaultDuration = STORY_DEFAULT_DURATION;
  @Input() headerDropdownList = [
    { id: STORY_HEADER_ACTIONS.DELETE, text: this.mls.languageText?.stories.key2 },
    { id: STORY_HEADER_ACTIONS.CANCEL, text: this.mls.languageText?.stories.key3 }
  ];

  @OnChange<boolean>('onActiveStoryIndexChange')
  @Input() activeStoryIndex;

  @OnChange<boolean>('onAutoStart')
  @Input() autoStart = false;

  @OnChange<boolean>('onLoadingStateChanged')
  @Input() loadingState;

  @Input() callingFrom: string;
  /**
   * default storyCode if there any
   */
  @Input() defaultStoryCode;
  /**
   * UI configuration for video, receiving as input
   */
  @OnChange<boolean>('onCurrentPlaying')
  @Input() currentPlaying = false;

  @Input() uuid: string;

  /**
   * audio status of video
   */
  public audioStatus = false;
  /**
   * No story type constant.
   * 0 for the whole stories list
   * 1 for the story view detail
   * 2 for the story thumbnail.
   */
  public NO_STORIES_TYPE = NO_INFO_TYPE;

  public viewListOffset = 1;

  public lockScreen = false;

  /**
   * Show/Hide delete story modal.
   */
  public isStoryDeleteModal: boolean;
  /**
   * public story view List
   */
  public storyViewList = [];

  /**
   * stories count
   */
  public storiesChangeEvent = 0;
  /**
   * STORY TYPES
   */
  public STORY_TYPES = STORY_TYPES;
  /**
   * long gestures
   */
  public longPressedEvent: boolean;
  /**
   * first time clicked flag to disable when story changing
   */
  public firstTimeFlag = false;

  /**
   * start time for analytics
   */
  public startTime = this.getTimeString();

  public storyStartTime;

  /**
   * previous index from where has navigated from
   */
  public previouIndex: number;

  public storyProgressPercentage: string;
  /**
   * Timer for the progress.
   * Subscription for the unsubscribe.
   * wait Long press release subject.
   */
  private timer: number;
  private subscriptions: Subscription = new Subscription();
  private waitLongPressResume = new Subject<number>();

  /**
   * Emit Stories start.
   * Emit all Stories end.
   * Emit current index of stories.
   * Emit on story change event.
   * Emit on story delete event.
   * Emit on go back button click.
   * Emit on all CTA's target.
   */
  @Output() onStoriesStart: EventEmitter<void> = new EventEmitter();
  @Output() onAllStoriesEnd: EventEmitter<number> = new EventEmitter(); //0 for prev 1 for next
  @Output() currentIndex: EventEmitter<number> = new EventEmitter();
  @Output() onStoryChange: EventEmitter<number> = new EventEmitter();
  @Output() onStoryDelete: EventEmitter<{index:number, uuid: string, storyCode: string}> = new EventEmitter();
  @Output() onGoBack: EventEmitter<number> = new EventEmitter();
  @Output() emitCtaTarget: EventEmitter<any> = new EventEmitter();
  @Output() emitdeeplinkRedirection: EventEmitter<any> = new EventEmitter();

  @Output() emitStoryView: EventEmitter<any> = new EventEmitter();
  @Output() emitClose: EventEmitter<void> = new EventEmitter();

  @Output() headerRedirect: EventEmitter<{userType: string, targetId: any}> = new EventEmitter();
  /**
   * Necessary instances.
   */
  constructor(
    private timerService: TimeService,
    private cdk: ChangeDetectorRef,
    public mls: NewLanguageService,
    public userStorySer: UserStoryService,
    public postSer: PostApiService,
    public Router: Router,
    public getSer: GetService,
    private fcs: FunCollectionService,
    private analyticsEventService: AnalyticsEventService,
    private analyticSdk: Analytics
  ) {
  }

  /**
   * Call timer for particular story.
   * Call long press release.
   */
  ngOnInit(): void {
    if (this.stories) {
      this.timerForParticularStory();
    }
    this.longPressRelease();
    this.subscriptions.add(this.userStorySer.storyDataChanged.subscribe(data => {
      this.cdk.markForCheck();
      this.cdk.detectChanges();
    }));
    this.subscriptions.add(this.userStorySer.longPressEvent.subscribe(data => {
      if (data === true) {
        this.longPressedEvent = true;
      }
      if (data === false) {
        this.longPressedEvent = false;
      }
    }));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (this.stories && this.stories.list[this.activeIndex]) {
          const exitData: DETAIL_EXIT = {
            entity_type: PRODUCT_TYPE_CONSTANT_NAME.USER_STORY,
            entity_code: this.stories.list[this.activeIndex].storyCode,
            current_screen_name: SCREEN_NAME.STORY_PLAY,
            start_time: this.storyStartTime,
            end_time: new Date().getTime()
          }  
          this.analyticSdk.analyticService.DETAIL_EXIT(exitData);
          this.startTime = this.getTimeString();
          this.storyStartTime = new Date().getTime();
        }
      } 
   });
  }

  /**
   * Timer for particular story subscription.
   */
  timerForParticularStory(): void {
    this.subscriptions.add(this.timerService.stopWatch$
      .subscribe((val: number) => {
        this.timer = val;
        const percent  = (this.timer * 100) / (this.stories.list?.[this.activeIndex]?.duration / 10);
        this.storyProgressPercentage = percent + '%';
        if (this.stories?.list?.[this.activeIndex] && this.timer >= (this.stories.list?.[this.activeIndex]?.duration / 10)) {
          if (this.activeIndex === this.stories.list.length - 1) {
            this.stopCount();
            this.emitOnAllStoriesEnd(ALL_STORIES_END.NEXT);
            return;
          }
          this.resetTimer();
          this.startCount();
          this.activeIndex = this.activeIndex + 1;
          this.emitCurrentIndex(this.activeIndex);
          this.emitOnStoryChange(this.activeIndex, this.activeIndex - 1);
          this.cdk.detectChanges();
        }
      }));
  }

  /**
   * change audio status
   */
  changeAudioStatus(event) {
    this.audioStatus = event;
  }
  /**
  * Long press release subscription.
  */
  longPressRelease(): void {
    this.subscriptions.add(this.waitLongPressResume
      .pipe(debounceTime(100))
      .subscribe(value => {
        if (value) {
          this.userStorySer.longPressEvent.next(false);
          this.startCount();
        }
      }));
  }

  /**
   * Emit Stories start.
   */
  emitOnStoriesStart(): void {
    this.onStoriesStart.emit();
  }
  /**
   * navigat to profile
   */
  profileRedirect(event) {
    this.fcs.currentScreename = SCREEN_NAME.STORY_PLAY;
    this.Router.navigateByUrl('/webprofile/view/info/' + `${event}`);
    this.closeStory();
  }
  /**
   * 
   * @param event deeplink redirection
   */
  deepLinkRedirect(event): void {
    window.location.href = event;
  }
  /**
   * disable story change gesturs
   */
  disableStoryChangeGesture(event): void {
    this.lockScreen = event;
  }
  /**
   * search story on basis of storycode
   */
  searchStoryBasedOnStoryCode(storyCode: string): number {
    const value = this.stories?.list?.findLastIndex(el =>{
      return el?.storyCode === storyCode;
    });
    return value >=0 ? value : 0;
  }
  /**
   * Search for the last active story.
   */
  searchLastActiveStory(): number {
    const value =  this.stories?.list?.findLastIndex(el => el?.viewed === 1);
    return value;
  }

  /**
   * close stories
   */
  closeStory(): void {
    if (this.stories?.list && this.stories?.list[this.activeIndex]?.storyCode) {
      const exitData: DETAIL_EXIT = {
        entity_type: PRODUCT_TYPE_CONSTANT_NAME.USER_STORY,
        entity_code: this.stories.list[this.activeIndex].storyCode,
        current_screen_name: SCREEN_NAME.STORY_PLAY,
        start_time: this.storyStartTime,
        end_time: new Date().getTime()
      }  
      this.analyticSdk.analyticService.DETAIL_EXIT(exitData);
      this.startTime = this.getTimeString();
      this.storyStartTime = new Date().getTime();
    }
    this.emitClose.emit();
  }

  /**
   * Trigger when any changes happens in configuration of autostart flag.
   */
  onAutoStart(value: boolean, simpleChange: SimpleChange): void {
    if (value && simpleChange.currentValue) {
      this.emitOnStoriesStart();
      this.startCount();
      this.cdk.markForCheck();
    }
  }
  /**
   * get story view List
   */
  getStoryViewList(storyCode): void {
    if (this.viewListOffset > 0) {
      this.getSer.getViewListForStories(storyCode, this.viewListOffset).subscribe(data => {
        if (data?.lc) {
          this.storyViewList.push(...data.lc);
          this.storyViewList = [...this.storyViewList];
        }
        else if (data?.status === 204) {
          this.viewListOffset = 0;
        }
        this.cdk.markForCheck();
        this.cdk.detectChanges();
      });
    }
  }
  /**
   *  Story View List cliked
   */
  viewListClicked(event): void {
    this.storyViewList = [];
    this.viewListOffset = 1;
    if (event) {
      this.toggleProgressCounter(true);
      this.getStoryViewList(this.stories.list[this.activeIndex].storyCode);
    }
    else if(!event) {
      this.toggleProgressCounter(false);
    }
  }
  /**
   * story view List Scrolled;
   */
  viewListScrolled(): void {
    this.viewListOffset = this.viewListOffset + 1;
    this.getStoryViewList(this.stories.list[this.activeIndex].storyCode);
  }
  /**
   * trigger when active story index changes
   */
  onActiveStoryIndexChange(value: number, simpleChange: SimpleChange): void {
    if (this.stories?.list) {
      let entryData : ENTRY = {
        entity_type: PRODUCT_TYPE_CONSTANT_NAME.USER_STORY,
        entity_code: '',
        current_screen_name: SCREEN_NAME.STORY_PLAYER,
        source: this.fcs.currentScreename ?? localStorage.getItem('triggerSource'),
        start_time: new Date().getTime()
      }
      let detailEntry: DETAIL_ENTRY = {
        entity_type: PRODUCT_TYPE_CONSTANT_NAME.USER_STORY,
        entity_code: 0,
        current_screen_name: SCREEN_NAME.STORY_PLAY,
        source: '',
        start_time: null
      }
      if (this.defaultStoryCode) {
        entryData.entity_code = this.defaultStoryCode;
        this.analyticSdk.analyticService.ENTRY(entryData);
        this.activeIndex = this.searchStoryBasedOnStoryCode(this.defaultStoryCode);
        this.storyStartTime = new Date().getTime();
        detailEntry.entity_code = this.defaultStoryCode;
        detailEntry.start_time = this.storyStartTime;
        this.analyticSdk.analyticService.DETAIL_ENTRY(detailEntry);
      }
      // to get index when user clicks first time on the story handle
      else if (this.firstTimeFlag === false) {
        this.firstTimeFlag = true;
        const lastActiveIndex = this.searchLastActiveStory();
        this.activeIndex = lastActiveIndex >= 0  && lastActiveIndex <= this.stories.list.length -1 ? (lastActiveIndex + 1 <= this.stories.list.length -1 ? lastActiveIndex + 1 : 0 ) : 0;
        entryData.entity_code = this.stories.list[this.activeIndex].storyCode;
        this.analyticSdk.analyticService.ENTRY(entryData);
        this.storyStartTime = new Date().getTime();
        detailEntry.start_time = this.storyStartTime;
        detailEntry.entity_code = this.stories.list[this.activeIndex].storyCode;
        this.analyticSdk.analyticService.DETAIL_ENTRY(detailEntry);
      }
      else {
        this.storyStartTime = new Date().getTime();
        const lastActiveIndex = this.searchLastActiveStory();
        this.resetTimer();
        this.activeIndex = lastActiveIndex >= 0  && lastActiveIndex <= this.stories.list.length -1 ? (lastActiveIndex + 1 <= this.stories.list.length -1 ? lastActiveIndex + 1 : lastActiveIndex ) : 0;
        detailEntry.entity_code = this.stories.list[this.activeIndex].storyCode;
        detailEntry.start_time = this.storyStartTime;
        this.analyticSdk.analyticService.DETAIL_ENTRY(detailEntry);
      }
      if (this.stories?.list[this.activeIndex]?.viewed === 0)  {
        this.stories.list[this.activeIndex].viewed = 1;
        this.logViewStory(this.stories.list[this.activeIndex].storyCode);
      }
    }
    this.cdk.markForCheck();
    this.cdk.detectChanges();
    this.startCount();
  }
  onLoadingStateChanged(simpleChange): void {
    if (this.loadingState === 'SUCCESS') {
      this.startCount();
      this.cdk.markForCheck();
      this.cdk.detectChanges();
    }
  }

  /**
   * Trigger when any changes happens in configuration current playing flag.
   */
  onCurrentPlaying(value: boolean, simpleChange: SimpleChange): void {
    if (value === false && simpleChange.currentValue === false) {
      this.resetTimer();
      this.activeIndex = 0;
    }
  }

  /**
   * Start the timer.
   */
  public startCount(): void {
    this.timerService.startCount();
  }

  /**
   * Stop timer.
   */
  public stopCount(): void {
    this.timerService.stopTimer();
  }

  /**
   * Reset timer.
   */
  public resetTimer(): void {
    this.timerService.resetTimer();
  }

  /**
   * Left tab gesture for the story.
   */
  leftGesture(): void {
    if (!this.lockScreen) {
      if (this.activeIndex === 0 || this.stories == undefined || this.stories?.list.length === 0) {
        this.emitOnAllStoriesEnd(ALL_STORIES_END.PREVIOUS);
        return;
      }
      if (this.activeIndex > 0) {
        this.resetTimer();
        this.startCount();
        this.activeIndex = this.activeIndex - 1;
        this.emitCurrentIndex(this.activeIndex);
        this.emitOnStoryChange(this.activeIndex, this.activeIndex + 1 );
      }
    }
  }

  /**
   * Right tab gesture for the story.
   */
  rightGesture(): void {
    if (!this.lockScreen) {
      if (this.activeIndex === this.stories?.list.length - 1 || this.stories == undefined || this.stories.list.length === 0) {
        this.emitOnAllStoriesEnd(ALL_STORIES_END.NEXT);
        return;
      }
      if (this.activeIndex !== this.stories?.list.length - 1) {
        this.resetTimer();
        this.startCount();
        this.activeIndex = this.activeIndex + 1;
        this.emitCurrentIndex(this.activeIndex);
        this.emitOnStoryChange(this.activeIndex, this.activeIndex - 1);
      }
    }
  }

  /**
   * Long press gesture for stories.
   */
  longGesture(): void {
    this.waitLongPressResume.next(Date.now());
    this.userStorySer.longPressEvent.next(true);
    this.stopCount();
  }

  /**
   * Emit all Stories end.
   *
   */
  emitOnAllStoriesEnd(status: number): void {
    if (this.stories && this.stories.list && this.stories.list[this.activeIndex]) {
      const exitData: DETAIL_EXIT = {
        entity_type: PRODUCT_TYPE_CONSTANT_NAME.USER_STORY,
        entity_code: this.stories.list[this.activeIndex].storyCode,
        current_screen_name: SCREEN_NAME.STORY_PLAY,
        start_time: this.storyStartTime,
        end_time: new Date().getTime()
      }  
      this.analyticSdk.analyticService.DETAIL_EXIT(exitData);
      this.startTime = this.getTimeString();
      this.storyStartTime = new Date().getTime();
    }
    this.storiesChangeEvent = this.storiesChangeEvent + 1;
    this.onAllStoriesEnd.emit(status);
  }

  /**
   * Emit current index of stories.
   */
  emitCurrentIndex(index: number): void {
    this.currentIndex.emit(index);
  }
  /**
   * log view event of stories
   */
    public logViewStory(storyCode): void {
      if (storyCode) {
        this.postSer.logStoryView(storyCode).subscribe(data => {
          if (data) {
            if (this.userStorySer.UserStoryDetail.has(this.userStorySer.currentStoryUuid)) {
              const channelId = this.userStorySer.UserStoryDetail.get(this.userStorySer.currentStoryUuid).value.channelId;
              if (channelId != null && channelId != undefined && channelId != 0) {
                this.userStorySer.UserStoryDetail.get('channel' + channelId).value.list[this.activeIndex].viewed = 1;
              }
              this.userStorySer.UserStoryDetail.get(this.userStorySer.currentStoryUuid).value.list[this.activeIndex].viewed = 1;
              this.stories.list[this.activeIndex].viewed = 1;
            }
          }
        });
      }
    }
    /**
    * Get the current time in string.
    */
    getTimeString(): string {
      return new Date().getTime().toString();
    }
  /**
   * emit story reaction log
   */
  public reactionLog(event): void {
    const entityType = this.stories.list[this.activeIndex].extra?.entityType;
    const entityCode = this.stories.list[this.activeIndex].extra?.entityCode;
    const likeData: LIKE = {
      entity_type: PRODUCT_TYPE_CONSTANT_NAME.USER_STORY,
      entity_code: event.storyCode,
      start_time: new Date().getTime(),
      current_screen_name: SCREEN_NAME.STORY_PLAY,
      action_on: event.value,
      action_type: ACTION_TYPE.CLICK,
      cta_type: CTA_TYPE.ENGAGEMENT_BAR
    }
    this.analyticSdk.analyticService.LIKE(likeData);
    this.postSer.logStoryReaction(event.storyCode, event.value).subscribe(data => {
      if (data) {
        if (this.userStorySer.UserStoryDetail.has(this.userStorySer.currentStoryUuid)) {
          this.userStorySer.UserStoryDetail.get(this.userStorySer.currentStoryUuid).value.list[this.activeIndex].viewed = 1;
          this.stories.list[this.activeIndex].viewed = 1;
        }
      }
    });
  }
  @HostListener('window:beforeunload.out-zone', ['$event'])
  beforeUnloadHandler() {
    if (this.stories && this.stories.list[this.activeIndex]) {
      const exitData: DETAIL_EXIT = {
        entity_type: PRODUCT_TYPE_CONSTANT_NAME.USER_STORY,
        entity_code: this.stories.list[this.activeIndex].storyCode,
        current_screen_name: SCREEN_NAME.STORY_PLAY,
        start_time: this.storyStartTime,
        end_time: new Date().getTime()
      }  
      this.analyticSdk.analyticService.DETAIL_EXIT(exitData);
      this.startTime = this.getTimeString();
      this.storyStartTime = new Date().getTime();
    }
    return true;
  }
  /**
   * Emit on story change event.
   */
  emitOnStoryChange(index: number, previouIndex): void {
    this.storiesChangeEvent = this.storiesChangeEvent + 1;
    if (this.stories.list[this.activeIndex]) {
      this.stories.list[this.activeIndex].active = false;
    }
    const exitData: DETAIL_EXIT = {
      entity_type: PRODUCT_TYPE_CONSTANT_NAME.USER_STORY,
      entity_code: this.stories.list[previouIndex].storyCode,
      current_screen_name: SCREEN_NAME.STORY_PLAY,
      start_time: this.storyStartTime,
      end_time: new Date().getTime()
    }  
    this.analyticSdk.analyticService.DETAIL_EXIT(exitData);
    this.startTime = this.getTimeString();
    this.storyStartTime = new Date().getTime();
    const detailEntry: DETAIL_ENTRY = {
      entity_type: PRODUCT_TYPE_CONSTANT_NAME.USER_STORY,
      entity_code: this.stories.list[index].storyCode,
      current_screen_name: SCREEN_NAME.STORY_PLAY,
      source: this.fcs.currentScreename,
      start_time: this.storyStartTime
    }
    this.analyticSdk.analyticService.DETAIL_ENTRY(detailEntry);
    if (this.stories?.list?.[this.activeIndex]?.viewed === 0) {
      this.logViewStory(this.stories.list[this.activeIndex].storyCode);
    }
    this.storyViewList = [];
    this.viewListOffset = 1;
    this.onStoryChange.emit(index);
  }

  /**
   * Generate Meta.
   */
  generateMeta(status: boolean): void {
    this.toggleProgressCounter(status);
  }

  /**
   * Action on generated Meta.
   */
  deepLinkRedirection(deepLink: string): void {
    window.location.href = deepLink;
  }

  /**
   * Call the API requests.
   */
  sendRequest(): void {
    // no code
  }

  /**
   * Action for the header dropdown.
   */
  doAction(action: number): void {
    if (action === 0) {
      if (this.stories && this.stories.list && this.stories.list[this.activeIndex])
      this.analyticSdk.analyticService.CLICK(PRODUCT_TYPE_CONSTANT_NAME.USER_STORY,this.stories.list[this.activeIndex].storyCode,SCREEN_NAME.STORY_PLAY, CTA_TYPE.BUTTON, ACTION_TYPE.CLICK, ACTION_ON.DELETE_STORY,null)
      this.isStoryDeleteModal = true;
    }
  }

  /**
   * Toggle the timer.
   */
  toggleProgressCounter(status: boolean): void {
    if (status) {
      this.stopCount();
    } else {
      this.startCount();
    }
  }

  deleteStory(index: number): void {

    //to be changed when listing of user of story of me type comes first
    const uuid = this.uuid;
    const stories = this.userStorySer.UserStoryDetail.get(uuid).value.list;
    const storyCode = stories[index]?.storyCode;
    this.analyticSdk.analyticService.CLICK(PRODUCT_TYPE_CONSTANT_NAME.USER_STORY, storyCode, SCREEN_NAME.STORY_PLAY, CTA_TYPE.BUTTON, ACTION_TYPE.CLICK, ACTION_ON.DELETE_STORY_CONFIRM, null )
    this.subscriptions.add(this.postSer.deleteUserStory(storyCode).subscribe(data => {
      if (data) {
        if (index === 0) {
          this.userStorySer.UserStoryDetail.get(uuid).value.list.splice(index,1);
          if (stories?.length === 0) {
            this.userStorySer.storyThumbnail.splice(this.userStorySer.currentStoryIndex,1);
          }
          this.emitOnAllStoriesEnd(ALL_STORIES_END.PREVIOUS);
        }
        else {
          this.activeIndex = this.activeIndex - 1;
          this.emitOnStoryChange(this.activeIndex, this.activeIndex + 1);
          this.userStorySer.UserStoryDetail.get(uuid).value.list.splice(index,1);
        }
        this.onStoryDelete.emit({index: index, uuid: uuid, storyCode: storyCode});
        this.isStoryDeleteModal = false;
        this.cdk.markForCheck();
        this.resetTimer();
        this.startCount();
      }
    }));

  }

  /**
   * Emit on story delete event.
   */
  emitStoryDelete(): void {
    this.deleteStory(this.activeIndex);
  }

  /**
   * Emit on go back button click.
   */
  goBack(): void {
    this.onGoBack.emit();
  }
  /**
   * emit duration
   */
  emitVideoDuration(event): void {
    const videoDuration = event * 1000;
    if (this.userStorySer.UserStoryDetail.has(this.userStorySer.currentStoryUuid)) {
      this.userStorySer.UserStoryDetail.get(this.userStorySer.currentStoryUuid).value.list[this.activeIndex].duration = videoDuration ;
      this.stories.list[this.activeIndex].duration = videoDuration;
    }
  }
  /**
   * Emit on all CTA's target.
   */
  onEmitCtaTarget(target: any): void {
    this.userStorySer.repostStoryData = this.stories?.list[this.activeIndex];
    const repostingData = {fileCode: this.stories?.list[this.activeIndex]?.fileCode, productType: this.stories?.list[this.activeIndex]?.extra?.entityType, productTypeId: this.stories?.list[this.activeIndex]?.extra?.entityCode,  templateValue: this.stories?.list[this.activeIndex]?.templateValue, storyType: this.stories?.list[this.activeIndex].storyType, fileType:
      this.stories?.list[this.activeIndex].fileType, storyCode: this.stories?.list[this.activeIndex].storyCode, cta: this.stories?.list[this.activeIndex]?.cta, pharmaRepostStoryCode: this.stories?.list[this.activeIndex].pharmaRepostStoryCode, duration: this.stories?.list[this.activeIndex].number};
    this.emitCtaTarget.emit({target, repostingData});
  }
  /**
   * profile header redirection
   */
  profileHeaderRedirection(): void {
    if (this.stories.type === STORY_TYPES.PHARMA) {
      this.headerRedirect.emit({userType: STORY_TYPES.PHARMA, targetId: this.stories.channelId });
    }
    else if (this.stories.customId) {
      this.headerRedirect.emit({userType: this.stories.type, targetId: this.stories.customId});
    }
  }
  /**
   * Unsubscribe the subscription.
   */
  ngOnDestroy(): void {
    if (this.stories?.list && this.stories?.list[this.activeIndex]?.storyCode) {
      const exitData: DETAIL_EXIT = {
        entity_type: PRODUCT_TYPE_CONSTANT_NAME.USER_STORY,
        entity_code: this.stories.list[this.activeIndex].storyCode,
        current_screen_name: SCREEN_NAME.STORY_PLAY,
        start_time: this.storyStartTime,
        end_time: new Date().getTime()
      }  
      this.analyticSdk.analyticService.DETAIL_EXIT(exitData);
      this.startTime = this.getTimeString();
      this.storyStartTime = new Date().getTime();
    }
    this.stories = null;
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
    this.timerService.stopTimer();
    this.timerService.resetTimer();
  }
}
